/**
 * Вид устройства для превью в карточке оборудования.
 *
 * ОТДЕЛЬНОГО ПОЛЯ «ТИП» В ДАННЫХ НЕТ. У предмета есть только название и произвольные
 * атрибуты, поэтому вид распознаётся по тексту: сначала по названию, потом по значениям
 * атрибутов, похожих на тип («Тип», «Вид», «Категория»). Это догадка, и относиться к ней
 * нужно как к догадке: она годится для картинки рядом с названием и ни для чего больше.
 * Ни доступ, ни фильтры, ни выгрузка её не используют и использовать не должны.
 *
 * НЕ УГАДАЛИ — НЕ РИСУЕМ. Неизвестный вид даёт null, и карточка показывает плитку
 * «изображения нет». Подставлять «что-нибудь похожее» нельзя: неверная картинка врёт
 * увереннее, чем пустая.
 *
 * МОДЕЛЬ ЗДЕСЬ НЕ УГАДЫВАЕТСЯ И УГАДЫВАТЬСЯ НЕ БУДЕТ. Вид техники — шесть обобщённых
 * рисунков, и ошибка догадки стоит «нарисовали ноутбук вместо ноутбука другой марки».
 * Модель — это уже конкретная фотография, и та же ошибка выдала бы снимок чужой техники
 * за снимок этой. Поэтому типовое фото привязано к ШАБЛОНУ (inventory_presets.
 * preview_file_id), а предмет знает свой шаблон ссылкой (inventory_items.preset_id) —
 * см. resolvePreviewSource() ниже.
 *
 * Список видов ограничен теми, для которых нарисовано превью: ноутбук, монитор,
 * хаб/док-станция, системный блок, принтер, телефон.
 */

export const DEVICE_LAPTOP = 'laptop';
export const DEVICE_MONITOR = 'monitor';
export const DEVICE_HUB = 'hub';
export const DEVICE_DESKTOP = 'desktop';
export const DEVICE_PRINTER = 'printer';
export const DEVICE_PHONE = 'phone';

/**
 * Названия атрибутов, значение которых может содержать вид устройства.
 */
const TYPE_ATTRIBUTE_NAMES = ['тип', 'вид', 'категория', 'type', 'kind', 'category'];

/**
 * Слова, по которым узнаётся вид. Порядок важен: первое совпадение и побеждает,
 * а ноутбук проверяется раньше монитора — у ноутбука тоже есть экран, и в названии
 * вида «Ноутбук с монитором» выиграть должен ноутбук.
 *
 * Новые виды дописаны в конец, а не вставлены в середину: порядок первых трёх правил —
 * принятое решение, и менять его местами значило бы менять уже проверенное поведение.
 *
 * Списки написаний узкие намеренно. Сюда не годятся слова, которые бывают частью чужого
 * названия: «пк» нашлось бы в любом артикуле, «сканер» нарисовал бы принтер вместо сканера.
 * Лучше плитка «изображения нет», чем уверенно неверная картинка.
 */
const RULES = [
    {
        kind: DEVICE_LAPTOP,
        keywords: ['ноутбук', 'ноутбука', 'нотбук', 'лэптоп', 'laptop', 'notebook', 'macbook', 'thinkpad'],
    },
    {
        kind: DEVICE_MONITOR,
        keywords: ['монитор', 'монитора', 'дисплей', 'monitor', 'display'],
    },
    {
        kind: DEVICE_HUB,
        keywords: ['хаб', 'хаба', 'usb-хаб', 'юсб-хаб', 'док-станция', 'докстанция', 'hub', 'dock', 'docking'],
    },
    {
        kind: DEVICE_DESKTOP,
        keywords: ['системный блок', 'системного блока', 'системник', 'рабочая станция', 'workstation', 'desktop'],
    },
    {
        kind: DEVICE_PRINTER,
        keywords: ['принтер', 'принтера', 'мфу', 'printer'],
    },
    {
        kind: DEVICE_PHONE,
        keywords: ['телефон', 'телефона', 'смартфон', 'смартфона', 'айфон', 'iphone', 'phone', 'smartphone'],
    },
];

/**
 * Символы, которые НЕ считаются частью слова. Нужны, чтобы «хаб» не находился внутри
 * фамилии или артикула: короткое слово по простому вхождению ловит слишком много.
 */
const BOUNDARY = '[^a-zа-яё0-9]';

/**
 * @param {string} value
 * @returns {string}
 */
function escapeForRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');
}

/**
 * Есть ли в тексте это слово целиком.
 *
 * @param {string} text Текст в нижнем регистре.
 * @param {string} keyword
 * @returns {boolean}
 */
function hasWord(text, keyword) {
    const pattern = new RegExp(`(^|${BOUNDARY})${escapeForRegExp(keyword)}(${BOUNDARY}|$)`, 'i');

    return pattern.test(text);
}

/**
 * Тексты предмета, в которых имеет смысл искать вид: название и значения атрибутов типа.
 *
 * @param {Object|null} item
 * @returns {string[]}
 */
function searchableTexts(item) {
    if (!item) {
        return [];
    }

    const texts = [];

    if (item.name) {
        texts.push(String(item.name));
    }

    const attributes = Array.isArray(item.attributes) ? item.attributes : [];

    attributes.forEach((attribute) => {
        const type = String(attribute?.type ?? '')
            .trim()
            .toLowerCase();

        if (TYPE_ATTRIBUTE_NAMES.indexOf(type) !== -1 && attribute?.value) {
            texts.push(String(attribute.value));
        }
    });

    return texts;
}

/**
 * Вид устройства или null, если распознать не удалось.
 *
 * @param {Object|null} item
 * @returns {string|null}
 */
export function detectDeviceKind(item) {
    const texts = searchableTexts(item).map((text) => text.toLowerCase());

    if (texts.length === 0) {
        return null;
    }

    // Сначала название, потом атрибуты: название — то, что писал человек про сам предмет.
    for (let index = 0; index < texts.length; index += 1) {
        const text = texts[index];
        const rule = RULES.find((candidate) => candidate.keywords.some((keyword) => hasWord(text, keyword)));

        if (rule) {
            return rule.kind;
        }
    }

    return null;
}

/**
 * Ступени цепочки превью в шапке карточки, от точного к общему.
 *
 * ПОРЯДОК ЗДЕСЬ И ЕСТЬ ПРАВИЛО, а не в разметке компонента: в шаблоне он выражался бы
 * лестницей v-else-if, которую нельзя ни прочитать одним взглядом, ни проверить тестом
 * без монтирования. Отсюда отдельная функция — как и всё остальное в этом модуле.
 */
export const PREVIEW_ITEM_PHOTO = 'item-photo';
export const PREVIEW_MODEL_PHOTO = 'model-photo';
export const PREVIEW_DEVICE_KIND = 'device-kind';
export const PREVIEW_EMPTY = 'empty';

/**
 * Что показывать в шапке карточки оборудования.
 *
 * ЦЕПОЧКА ИЗ ЧЕТЫРЁХ СТУПЕНЕЙ, и каждая следующая общее предыдущей:
 *   1) главное фото ЭТОГО экземпляра — снимок той самой единицы техники;
 *   2) типовое фото модели — картинка шаблона, по которому единицу завели;
 *   3) рисунок по виду техники — догадка по названию, см. detectDeviceKind();
 *   4) плитка «изображения нет».
 *
 * ФОТО ЭКЗЕМПЛЯРА ОТМЕНЯЕТ ТИПОВОЕ. Снимок конкретной единицы всегда точнее картинки
 * модели: на нём видно именно эту технику — с её наклейкой, её сколом и её комплектацией.
 * Показать вместо него глянцевую картинку из каталога значит подменить факт рекламой.
 *
 * РЕШЕНИЕ ПРИНИМАЕТСЯ ДО ЗАГРУЗКИ БАЙТОВ, и это не оптимизация. И о главном фото,
 * и о типовом известно из ответа карточки (primary_photo, model_photo) ещё до того, как
 * приедет содержимое. Если бы ступень выбиралась по готовности картинки, шапка при каждом
 * открытии успевала бы показать рисунок по виду и сменить его снимком — то самое мигание,
 * которого быть не должно. Пока байты едут, показывается нейтральная заглушка; узнать,
 * что идёт именно ожидание, можно по isPreviewPending().
 *
 * @param {{hasItemPhoto?: boolean, hasModelPhoto?: boolean, deviceKind?: string|null}} state
 * @returns {string} Одна из констант PREVIEW_*.
 */
export function resolvePreviewSource(state) {
    const source = state && typeof state === 'object' ? state : {};

    if (source.hasItemPhoto) {
        return PREVIEW_ITEM_PHOTO;
    }

    if (source.hasModelPhoto) {
        return PREVIEW_MODEL_PHOTO;
    }

    return source.deviceKind ? PREVIEW_DEVICE_KIND : PREVIEW_EMPTY;
}

/**
 * Ждём ли мы сейчас содержимое картинки.
 *
 * Отдельным вопросом, а не пятой ступенью цепочки: ожидание — не источник изображения,
 * а состояние выбранного источника. Смешать их значило бы получить ступень, которая
 * то появляется, то исчезает, не меняя решения.
 *
 * @param {string} sourceKind Результат resolvePreviewSource().
 * @param {string} url Адрес уже приехавшего содержимого; пустая строка, пока его нет.
 * @returns {boolean}
 */
export function isPreviewPending(sourceKind, url) {
    return (sourceKind === PREVIEW_ITEM_PHOTO || sourceKind === PREVIEW_MODEL_PHOTO) && !url;
}
